import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { PAGE_SIZE_OPTIONS, type PageSize } from './types'

const t = buildTranslate('locations.location_list')

export const Pagination = ({
  currentPage,
  totalPages,
  onChange,
}: {
  currentPage: number
  totalPages: number
  onChange: (page: number) => void
}) => {
  const [css] = useStyletron()
  const isFirstPage = currentPage <= 1
  const isLastPage = currentPage >= totalPages
  const navButtonClass = (disabled: boolean) =>
    `body-text-3 ${css({
      background: 'none',
      border: 'none',
      padding: 0,
      color: disabled ? palette.grey40 : palette.belizeHole,
      cursor: disabled ? 'not-allowed' : 'pointer',
    })}`
  return (
    <div className="d-flex align-items-center justify-content-center my-l">
      <button
        type="button"
        onClick={() => onChange(currentPage - 1)}
        disabled={isFirstPage}
        className={`mr-l ${navButtonClass(isFirstPage)}`}
      >
        <i className="fa fa-chevron-left mr-xs" aria-hidden="true" />
        {t('pagination.prev')}
      </button>
      <span className={`body-text-4 mr-s ${css({ color: palette.grey90 })}`}>
        {t('pagination.page')}
      </span>
      <span
        className={`d-inline-flex align-items-center justify-content-center mr-s body-text-3 ${css(
          {
            width: '80px',
            padding: '14px 16px',
            border: `1px solid ${palette.grey40}`,
            borderRadius: '4px',
            backgroundColor: palette.grey05,
            color: palette.grey90,
          }
        )}`}
      >
        {currentPage}
      </span>
      <span className={`body-text-4 mr-l ${css({ color: palette.grey90 })}`}>
        {t('pagination.of', { count: totalPages })}
      </span>
      <button
        type="button"
        onClick={() => onChange(currentPage + 1)}
        disabled={isLastPage}
        className={navButtonClass(isLastPage)}
      >
        {t('pagination.next')}
        <i className="fa fa-chevron-right ml-xs" aria-hidden="true" />
      </button>
    </div>
  )
}

export const PageSizeSelector = ({
  value,
  onChange,
}: {
  value: PageSize
  onChange: (size: PageSize) => void
}) => {
  const [css] = useStyletron()
  return (
    <label
      className={`d-flex align-items-center body-text-3 ${css({
        color: palette.grey90,
      })}`}
    >
      <span className="mr-s">{t('pagination.per_page_label')}</span>
      <select
        aria-label={t('pagination.per_page_aria')}
        value={value}
        onChange={e => {
          const parsed = Number(e.target.value)
          const next = PAGE_SIZE_OPTIONS.find(size => size === parsed)
          if (next) onChange(next)
        }}
        className={css({
          height: '40px',
          padding: '0 28px 0 12px',
          backgroundColor: palette.white,
          border: `1px solid ${palette.grey30}`,
          borderRadius: '4px',
          color: palette.grey90,
          cursor: 'pointer',
          appearance: 'none',
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%236A747F' d='M0 0l5 6 5-6z'/></svg>\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 10px center',
        })}
      >
        {PAGE_SIZE_OPTIONS.map(size => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
      <span className="ml-s">{t('pagination.per_page_suffix')}</span>
    </label>
  )
}
